import { useRef, useState, type ChangeEvent } from 'react';
import {
  Button,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
  RadioGroupItem,
  Spinner,
} from '@monksflow/monks-ui';
import clsx from 'clsx';
import { ArrowLeftRightIcon, ChevronDownIcon, MessageSquareIcon, UploadIcon } from 'lucide-react';
import { formatVersionLabel } from '@/entities/project-file';
import { useSelectedVersion } from '../../model/comments.store';
import type { VersionSelectorPopoverProps } from './VersionSelectorPopover.types';
import { ScopedPermission } from '@/shared/ui';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { useTranslation } from 'react-i18next';

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
});

export function VersionSelectorPopover({
  accept,
  disabled = false,
  isUploading = false,
  onUpload,
  onCompare,
}: VersionSelectorPopoverProps) {
  const { t } = useTranslation('projects');

  const { versions, selectedVersion, isVersionResolved, switchToVersion } = useSelectedVersion();
  const [isOpen, setIsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleVersionChange = (nextId: string) => {
    if (nextId && nextId !== selectedVersion?.id) {
      switchToVersion(nextId);
    }
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    onUpload(file);
  };

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="default"
            disabled={disabled || !isVersionResolved}
          >
            {selectedVersion
              ? t('commentOnFile.versionSelectorPopover.version', {
                  versionLabel: formatVersionLabel(selectedVersion.versionNumber),
                })
              : t('commentOnFile.versionSelectorPopover.versions')}
            {isVersionResolved ? <ChevronDownIcon className="size-4" /> : <Spinner />}
          </Button>
        </PopoverTrigger>

        <PopoverContent align="end" className="w-[560px] p-4">
          <div className="flex flex-col">
            <p className="text-xs text-foreground">
              {t('commentOnFile.versionSelectorPopover.versioning')}
            </p>

            <RadioGroup
              value={selectedVersion?.id ?? ''}
              onValueChange={handleVersionChange}
              className="max-h-70 gap-0 overflow-y-auto px-1"
            >
              {versions.map((version) => {
                const isCurrent = version.id === selectedVersion?.id;
                const iconStrokeWidth = isCurrent ? 2.5 : 2;
                return (
                  <Label
                    key={version.id}
                    htmlFor={version.id}
                    className="flex w-full cursor-pointer items-center gap-2 border-b border-border py-5 px-3 first:border-t-0 text-sm font-normal"
                  >
                    <RadioGroupItem value={version.id} id={version.id} />
                    <div className="flex w-full items-center gap-2">
                      <div className="flex flex-1 items-center gap-2">
                        <span
                          className={clsx(
                            'text-foreground',
                            isCurrent ? 'font-semibold' : 'font-normal',
                          )}
                        >
                          {t('commentOnFile.versionSelectorPopover.version', {
                            versionLabel: formatVersionLabel(version.versionNumber),
                          })}
                        </span>
                      </div>
                      <div className="flex flex-1 items-center justify-between gap-8">
                        <span>{dateFormatter.format(new Date(version.createdAt))}</span>
                        <span className="flex items-center gap-1.5">
                          <MessageSquareIcon size={14} strokeWidth={iconStrokeWidth} />
                          {version.commentCount}
                        </span>
                      </div>
                    </div>
                  </Label>
                );
              })}
            </RadioGroup>

            <div className="flex gap-2 mt-4">
              <ScopedPermission className="flex-1" permissions={[PROJECT_PERMISSIONS.ProjectView]}>
                <Button variant="outline" size="sm" disabled={!onCompare} onClick={onCompare}>
                  <ArrowLeftRightIcon className="mr-1 size-4" />
                  {t('commentOnFile.versionSelectorPopover.compare')}
                </Button>
              </ScopedPermission>
              <ScopedPermission
                className="flex-1"
                permissions={[PROJECT_PERMISSIONS.ProjectUploadAssets]}
              >
                <Button size="sm" onClick={handleUploadClick} disabled={isUploading}>
                  <UploadIcon className="mr-1 size-4" />
                  {isUploading
                    ? t('commentOnFile.versionSelectorPopover.uploading')
                    : t('commentOnFile.versionSelectorPopover.uploadNewVersion')}
                </Button>
              </ScopedPermission>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        hidden
        onChange={handleFileChange}
        data-testid="version-upload-input"
      />
    </>
  );
}
